import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LabeledInputOption,
  TypedFormControl,
  TypedFormGroup,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { getTwainDeviceImplementation } from '@principle-theorem/principle-bridge-core';
import { BridgeDevice } from '@principle-theorem/principle-core';
import {
  IBridgeDevice,
  ITwainDevice,
  TwainColourMode,
  TwainImplementation,
  TwainTransferMode,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  getEnumValues,
  shareReplayCold,
  snapshot,
} from '@principle-theorem/shared';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';

type TwainDeviceSettingsForm = Pick<
  ITwainDevice,
  | 'multiImageEnabled'
  | 'colourMode'
  | 'transferMode'
  | 'implementation'
  | 'verboseLogging'
>;

@Component({
  selector: 'pr-twain-device-settings',
  templateUrl: './twain-device-settings.component.html',
  styleUrl: './twain-device-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TwainDeviceSettingsComponent {
  bridgeDevice$ = new ReplaySubject<WithRef<IBridgeDevice>>(1);
  twainDeviceUid$ = new ReplaySubject<string>(1);
  disabled$ = new ReplaySubject<boolean>(1);
  bridgeVersion$: Observable<string | undefined>;
  colourOptions = getEnumValues(TwainColourMode);
  transferOptions = getEnumValues(TwainTransferMode);
  implementationOptions: LabeledInputOption[] = [
    { label: 'TwainCli', value: TwainImplementation.TwainCli },
    { label: 'TwainSave', value: TwainImplementation.TwainSave },
    { label: 'Naps2', value: TwainImplementation.NAPS2 },
  ];
  isNaps2Active$: Observable<boolean>;
  isTwainCliActive$: Observable<boolean>;

  twainDeviceSettingsForm = new TypedFormGroup<TwainDeviceSettingsForm>({
    multiImageEnabled: new TypedFormControl<boolean>(),
    colourMode: new TypedFormControl<TwainColourMode>(
      TwainColourMode.DeviceDefault
    ),
    transferMode: new TypedFormControl<TwainTransferMode>(
      TwainTransferMode.Native
    ),
    implementation: new TypedFormControl<TwainImplementation>(
      TwainImplementation.TwainCli
    ),
    verboseLogging: new TypedFormControl<boolean>(false),
  });

  @Input()
  set bridgeDevice(device: WithRef<IBridgeDevice>) {
    if (device) {
      this.bridgeDevice$.next(device);
    }
  }

  @Input()
  set twainDeviceUid(uid: string) {
    if (uid) {
      this.twainDeviceUid$.next(uid);
    }
  }

  @Input()
  set disabled(disabled: BooleanInput) {
    this.disabled$.next(coerceBooleanProperty(disabled));
  }

  constructor() {
    const twainDeviceSettings$ = combineLatest([
      this.bridgeDevice$,
      this.twainDeviceUid$,
    ]).pipe(
      map(
        ([bridgeDevice, uid]) =>
          bridgeDevice.twainSettings?.devices.find(
            (device) => device.uid === uid
          )
      )
    );

    twainDeviceSettings$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((settings) => this._initialiseForm(settings));

    validFormGroupChanges$(this.twainDeviceSettingsForm)
      .pipe(takeUntilDestroyed())
      .subscribe((formChanges) => void this.updateDevice(formChanges));

    this.bridgeVersion$ = this.bridgeDevice$.pipe(
      map((device) => device.settings?.version)
    );

    this.isNaps2Active$ = twainDeviceSettings$.pipe(
      map((settings) => settings?.implementation === TwainImplementation.NAPS2),
      shareReplayCold()
    );

    this.isTwainCliActive$ = twainDeviceSettings$.pipe(
      map(
        (settings) => settings?.implementation === TwainImplementation.TwainCli
      ),
      shareReplayCold()
    );
  }

  async updateDevice(changes: Partial<ITwainDevice>): Promise<void> {
    const bridgeDevice = await snapshot(this.bridgeDevice$);
    const deviceUid = await snapshot(this.twainDeviceUid$);
    this._ensureLegacyCompatibility(changes);

    await BridgeDevice.patchTwainDevice(bridgeDevice, deviceUid, changes);
  }

  private _ensureLegacyCompatibility(changes: Partial<ITwainDevice>): void {
    if (changes.implementation !== undefined) {
      changes.compatibilityMode =
        changes.implementation === TwainImplementation.NAPS2;
    }
  }

  private _initialiseForm(twainDevice?: ITwainDevice): void {
    if (!twainDevice) {
      return;
    }
    const formValue: Partial<TwainDeviceSettingsForm> = {
      ...twainDevice,
      implementation: getTwainDeviceImplementation(twainDevice),
    };
    this.twainDeviceSettingsForm.patchValue(formValue, { emitEvent: false });
  }
}
